import apiService from './api';

// New interface matching the actual Schwab API response
export interface SchwabApiResponse {
  assetMainType: string;
  assetSubType: string;
  quoteType: string;
  realtime: boolean;
  ssid: number;
  symbol: string;
  extended: {
    askPrice: number;
    askSize: number;
    bidPrice: number;
    bidSize: number;
    lastPrice: number;
    lastSize: number;
    mark: number;
    quoteTime: number;
    totalVolume: number;
    tradeTime: number;
  };
  fundamental: {
    avg10DaysVolume: number;
    avg1YearVolume: number;
    declarationDate: string;
    divAmount: number;
    divExDate: string;
    divFreq: number;
    divPayAmount: number;
    divPayDate: string;
    divYield: number;
    eps: number;
    fundLeverageFactor: number;
    lastEarningsDate: string;
    nextDivExDate: string;
    nextDivPayDate: string;
    peRatio: number;
  };
  quote: {
    '52WeekHigh': number;
    '52WeekLow': number;
    askMICId: string;
    askPrice: number;
    askSize: number;
    askTime: number;
    bidMICId: string;
    bidPrice: number;
    bidSize: number;
    bidTime: number;
    closePrice: number;
    highPrice: number;
    lastMICId: string;
    lastPrice: number;
    lastSize: number;
    lowPrice: number;
    mark: number;
    markChange: number;
    markPercentChange: number;
    netChange: number;
    netPercentChange: number;
    openPrice: number;
    postMarketChange: number;
    postMarketPercentChange: number;
    quoteTime: number;
    securityStatus: string;
    totalVolume: number;
    tradeTime: number;
  };
  reference: {
    cusip: string;
    description: string;
    exchange: string;
    exchangeName: string;
    isHardToBorrow: boolean;
    isShortable: boolean;
    htbRate: number;
  };
  regular: {
    regularMarketLastPrice: number;
    regularMarketLastSize: number;
    regularMarketNetChange: number;
    regularMarketPercentChange: number;
    regularMarketTradeTime: number;
  };
}

// Legacy interface for backward compatibility (flattened structure)
export interface SchwabQuote {
  symbol: string;
  description: string;
  bidPrice: number;
  bidId: string;
  askPrice: number;
  askId: string;
  lastPrice: number;
  lastId: string;
  openPrice: number;
  highPrice: number;
  lowPrice: number;
  bidTick: string;
  closePrice: number;
  netChange: number;
  totalVolume: number;
  quoteTimeInLong: number;
  tradeTimeInLong: number;
  mark: number;
  exchange: string;
  exchangeName: string;
  marginable: boolean;
  shortable: boolean;
  volatility: number;
  digits: number;
  '52WkHigh': number;
  '52WkLow': number;
  nAV: number;
  peRatio: number;
  divAmount: number;
  divYield: number;
  divDate: string;
  securityStatus: string;
  regularMarketLastPrice: number;
  regularMarketLastSize: number;
  regularMarketNetChange: number;
  regularMarketTradeTimeInLong: number;
  netPercentChangeInDouble: number;
  markChangeInDouble: number;
  markPercentChangeInDouble: number;
  regularMarketPercentChangeInDouble: number;
  delayed: boolean;
}

export interface SchwabOptionChain {
  symbol: string;
  status: string;
  underlying?: {
    symbol: string;
    description: string;
    change: number;
    percentChange: number;
    close: number;
    quoteTime: number;
    tradeTime: number;
    bid: number;
    ask: number;
    last: number;
    mark: number;
    markChange: number;
    markPercentChange: number;
    bidSize: number;
    askSize: number;
    highPrice: number;
    lowPrice: number;
    openPrice: number;
    totalVolume: number;
    exchangeName: string;
    fiftyTwoWeekHigh: number;
    fiftyTwoWeekLow: number;
    delayed: boolean;
  };
  strategy: string;
  interval: number;
  isDelayed: boolean;
  isIndex: boolean;
  interestRate: number;
  underlyingPrice: number;
  volatility: number;
  daysToExpiration: number;
  numberOfContracts: number;
  putExpDateMap?: { [key: string]: { [key: string]: SchwabOptionContract[] } };
  callExpDateMap?: { [key: string]: { [key: string]: SchwabOptionContract[] } };
}

export interface SchwabOptionContract {
  putCall: 'PUT' | 'CALL';
  symbol: string;
  description: string;
  exchangeName: string;
  bid: number;
  ask: number;
  last: number;
  mark: number;
  bidSize: number;
  askSize: number;
  bidAskSize: string;
  lastSize: number;
  highPrice: number;
  lowPrice: number;
  openPrice: number;
  closePrice: number;
  totalVolume: number;
  tradeDate?: string;
  tradeTimeInLong: number;
  quoteTimeInLong: number;
  netChange: number;
  volatility: number;
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  rho: number;
  openInterest: number;
  timeValue: number;
  theoreticalOptionValue: number;
  theoreticalVolatility: number;
  optionDeliverablesList?: any[];
  strikePrice: number;
  expirationDate: number;
  daysToExpiration: number;
  expirationType: string;
  lastTradingDay: number;
  multiplier: number;
  settlementType: string;
  deliverableNote: string;
  isIndexOption: boolean;
  percentChange: number;
  markChange: number;
  markPercentChange: number;
  nonStandard: boolean;
  inTheMoney: boolean;
  mini: boolean;
}

export interface SchwabOptionExpirationChain {
  symbol: string;
  status: string;
  underlying?: {
    symbol: string;
    description: string;
    change: number;
    percentChange: number;
    close: number;
    quoteTime: number;
    tradeTime: number;
    bid: number;
    ask: number;
    last: number;
    mark: number;
    markChange: number;
    markPercentChange: number;
    bidSize: number;
    askSize: number;
    highPrice: number;
    lowPrice: number;
    openPrice: number;
    totalVolume: number;
    exchangeName: string;
    fiftyTwoWeekHigh: number;
    fiftyTwoWeekLow: number;
    delayed: boolean;
  };
  isDelayed: boolean;
  isIndex: boolean;
  interestRate: number;
  underlyingPrice: number;
  volatility: number;
  daysToExpiration: number;
  numberOfContracts: number;
  expDateMap: { [key: string]: SchwabOptionExpiration };
}

export interface SchwabOptionExpiration {
  expirationDate: string;
  daysToExpiration: number;
  expirationType: string;
  settlementType: string;
  optionRoots: string;
  standard: boolean;
}

export interface GetQuotesRequest {
  symbols: string[];
}

export interface GetOptionChainRequest {
  contractType?: 'ALL' | 'PUT' | 'CALL';
  strikeCount?: number;
  includeUnderlyingQuote?: boolean;
  strategy?: string;
  interval?: number;
  strike?: number;
  strikeRange?: string;
  fromDate?: string;
  toDate?: string;
  volatility?: number;
  underlyingPrice?: number;
  interestRate?: number;
  daysToExpiration?: number;
  expMonth?: string;
  optionType?: 'S' | 'NS' | 'ALL';
  entitlement?: string;
}

class SchwabService {
  // Transform Schwab API response to flattened structure
  private transformSchwabResponse(apiResponse: SchwabApiResponse): SchwabQuote {
    return {
      symbol: apiResponse.symbol,
      description: apiResponse.reference.description,
      bidPrice: apiResponse.quote.bidPrice,
      bidId: apiResponse.quote.bidMICId,
      askPrice: apiResponse.quote.askPrice,
      askId: apiResponse.quote.askMICId,
      lastPrice: apiResponse.quote.lastPrice,
      lastId: apiResponse.quote.lastMICId,
      openPrice: apiResponse.quote.openPrice,
      highPrice: apiResponse.quote.highPrice,
      lowPrice: apiResponse.quote.lowPrice,
      bidTick: '', // Not available in new response
      closePrice: apiResponse.quote.closePrice,
      netChange: apiResponse.quote.netChange,
      totalVolume: apiResponse.quote.totalVolume,
      quoteTimeInLong: apiResponse.quote.quoteTime,
      tradeTimeInLong: apiResponse.quote.tradeTime,
      mark: apiResponse.quote.mark,
      exchange: apiResponse.reference.exchange,
      exchangeName: apiResponse.reference.exchangeName,
      marginable: true, // Not available in new response, default to true
      shortable: apiResponse.reference.isShortable,
      volatility: 0, // Not available in new response
      digits: 2, // Default for most stocks
      '52WkHigh': apiResponse.quote['52WeekHigh'],
      '52WkLow': apiResponse.quote['52WeekLow'],
      nAV: 0, // Not available in new response
      peRatio: apiResponse.fundamental.peRatio,
      divAmount: apiResponse.fundamental.divAmount,
      divYield: apiResponse.fundamental.divYield,
      divDate: apiResponse.fundamental.divExDate,
      securityStatus: apiResponse.quote.securityStatus,
      regularMarketLastPrice: apiResponse.regular.regularMarketLastPrice,
      regularMarketLastSize: apiResponse.regular.regularMarketLastSize,
      regularMarketNetChange: apiResponse.regular.regularMarketNetChange,
      regularMarketTradeTimeInLong: apiResponse.regular.regularMarketTradeTime,
      netPercentChangeInDouble: apiResponse.quote.netPercentChange,
      markChangeInDouble: apiResponse.quote.markChange,
      markPercentChangeInDouble: apiResponse.quote.markPercentChange,
      regularMarketPercentChangeInDouble:
        apiResponse.regular.regularMarketPercentChange,
      delayed: !apiResponse.realtime,
    };
  }

  // Get quotes for multiple symbols
  async getQuotes(
    symbols: string[],
  ): Promise<{ [symbol: string]: SchwabQuote }> {
    const apiResponse = await apiService.getSchwabQuotes(symbols);
    const transformed: { [symbol: string]: SchwabQuote } = {};

    for (const [symbol, quote] of Object.entries(apiResponse)) {
      if (quote) {
        transformed[symbol] = this.transformSchwabResponse(
          quote as SchwabApiResponse,
        );
      }
    }

    return transformed;
  }

  // Get quote for a single symbol
  async getQuote(symbol: string): Promise<SchwabQuote> {
    const apiResponse = await apiService.getSchwabQuote(symbol);
    return this.transformSchwabResponse(apiResponse as SchwabApiResponse);
  }

  // Get option chain for a symbol
  async getOptionChain(
    symbol: string,
    params?: GetOptionChainRequest,
  ): Promise<SchwabOptionChain> {
    return await apiService.getSchwabOptionChain(symbol, params);
  }

  // Get option expiration chain for a symbol
  async getOptionExpirationChain(
    symbol: string,
  ): Promise<SchwabOptionExpirationChain> {
    return await apiService.getSchwabOptionExpirationChain(symbol);
  }

  // Get Schwab configuration status
  async getConfigStatus(): Promise<{
    configured: boolean;
    sandboxMode: boolean;
    baseUrl: string;
    timestamp: string;
  }> {
    return await apiService.getSchwabConfigStatus();
  }

  // Get rate limit status
  async getRateLimitStatus(): Promise<{
    rateLimits: {
      [key: string]: {
        requests: number;
        windowMs: number;
        resetTime: string;
      };
    };
    timestamp: string;
  }> {
    return await apiService.getSchwabRateLimitStatus();
  }

  // Enrich ticker data with Schwab quote
  async enrichTickerData(symbol: string): Promise<{
    quote: SchwabQuote;
    optionChain?: SchwabOptionChain;
    expirationChain?: SchwabOptionExpirationChain;
  }> {
    try {
      // Get basic quote
      const quote = await this.getQuote(symbol);

      // Try to get option chain (may fail if not optionable)
      let optionChain: SchwabOptionChain | undefined;
      let expirationChain: SchwabOptionExpirationChain | undefined;

      try {
        optionChain = await this.getOptionChain(symbol);
        expirationChain = await this.getOptionExpirationChain(symbol);
      } catch (error) {
        // Symbol is not optionable, which is fine
        console.log(`Symbol ${symbol} is not optionable`);
      }

      return {
        quote,
        optionChain,
        expirationChain,
      };
    } catch (error) {
      throw new Error(
        `Failed to enrich ticker data for ${symbol}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  // Search for symbols (using quotes endpoint with multiple symbols)
  async searchSymbols(query: string): Promise<SchwabQuote[]> {
    // This is a simplified search - in a real implementation, you might want
    // to use a different endpoint or implement fuzzy matching
    const symbols = query.split(',').map((s) => s.trim().toUpperCase());

    if (symbols.length === 0) {
      return [];
    }

    try {
      const quotes = await this.getQuotes(symbols);
      return Object.values(quotes);
    } catch (error) {
      console.error('Error searching symbols:', error);
      return [];
    }
  }

  // Validate symbol exists and is tradable
  async validateSymbol(symbol: string): Promise<{
    valid: boolean;
    quote?: SchwabQuote;
    error?: string;
  }> {
    try {
      const quote = await this.getQuote(symbol);
      return {
        valid: true,
        quote,
      };
    } catch (error) {
      return {
        valid: false,
        error:
          error instanceof Error
            ? error.message
            : 'Symbol not found or not accessible',
      };
    }
  }

  // Get market hours and trading status
  async getMarketStatus(): Promise<{
    isOpen: boolean;
    currentTime: string;
    nextOpen: string;
    nextClose: string;
    holidays: string[];
  }> {
    // This would typically come from a market data provider
    // For now, we'll return a basic structure
    const now = new Date();
    const isWeekend = now.getDay() === 0 || now.getDay() === 6;
    const isMarketHours =
      !isWeekend && now.getHours() >= 9 && now.getHours() < 16;

    return {
      isOpen: isMarketHours,
      currentTime: now.toISOString(),
      nextOpen: '09:30',
      nextClose: '16:00',
      holidays: [], // Would be populated from market calendar
    };
  }
}

export const schwabService = new SchwabService();
export default schwabService;
